import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Pause, Play } from "lucide-react";
import { SCRIPTS, type ExplainerScript } from "./scripts";

type Props = {
  slug: string;
};

export function CourseExplainer({ slug }: Props) {
  const script = SCRIPTS[slug];
  if (!script) return null;
  return <ExplainerPlayer script={script} />;
}

function ExplainerPlayer({ script }: { script: ExplainerScript }) {
  const [scene, setScene] = useState(0);
  const [inView, setInView] = useState(true);
  const [paused, setPaused] = useState(false);
  const reduce = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const total = script.scenes.length;
  // Scene cycling runs whenever the explainer is visible and not paused —
  // including under reduced-motion (the transitions just become instant fades
  // so reduced-motion users still see every scene).
  const advancing = inView && !paused;
  // Looping ambient drift is heavier and only meaningful as motion, so it
  // additionally turns off under reduced-motion.
  const ambient = advancing && !reduce;

  // Pause auto-advance when scrolled out of view, so the explainer
  // doesn't keep spinning timers / repainting in the background.
  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.2 },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!advancing) return;
    const dur = script.scenes[scene].durationMs ?? 3800;
    const t = setTimeout(() => setScene((s) => (s + 1) % total), dur);
    return () => clearTimeout(t);
  }, [scene, advancing, script, total]);

  const Active = script.scenes[scene].render;

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-video overflow-hidden rounded-2xl border border-border bg-foreground text-background shadow-xl"
      role="img"
      aria-label={script.aria}
    >
      {/* Persistent grid backdrop (Editorial Terminal motif) */}
      <div className="absolute inset-0 bg-grid-sm opacity-[0.07] pointer-events-none" aria-hidden="true" />

      {/* Persistent ambient blobs — only animate while `playing` so we don't
          burn cycles offscreen, when paused, or under reduced-motion. */}
      <motion.div
        className="absolute -left-32 -top-32 w-[55%] aspect-square rounded-full blur-3xl opacity-30"
        style={{ background: "radial-gradient(circle, var(--color-primary), transparent 70%)" }}
        animate={ambient ? { x: [0, 80, -20, 40, 0], y: [0, 60, 30, -10, 0] } : { x: 0, y: 0 }}
        transition={{ duration: 22, repeat: ambient ? Infinity : 0, ease: "easeInOut" }}
        aria-hidden="true"
      />
      <motion.div
        className="absolute -right-24 -bottom-24 w-[50%] aspect-square rounded-full blur-3xl opacity-25"
        style={{ background: "radial-gradient(circle, var(--color-secondary), transparent 70%)" }}
        animate={ambient ? { x: [0, -60, 20, -30, 0], y: [0, -40, -20, 10, 0] } : { x: 0, y: 0 }}
        transition={{ duration: 26, repeat: ambient ? Infinity : 0, ease: "easeInOut" }}
        aria-hidden="true"
      />

      {/* Persistent corner lockup — like a watermark, transforms slightly per scene */}
      <motion.div
        className="absolute top-4 left-4 z-20 font-mono text-[10px] uppercase tracking-[0.25em] text-background/60"
        animate={{ opacity: scene === total - 1 ? 0 : 0.6 }}
        transition={{ duration: 0.5 }}
        aria-hidden="true"
      >
        {script.kicker}
      </motion.div>
      <motion.div
        className="absolute top-4 right-4 z-20 font-mono text-[10px] uppercase tracking-[0.25em] text-background/50 tabular-nums"
        animate={{ opacity: scene === total - 1 ? 0 : 0.5 }}
        transition={{ duration: 0.5 }}
        aria-hidden="true"
      >
        {String(scene + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
      </motion.div>

      {/* Scene foreground. Reduced-motion: opacity-only, near-instant. */}
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.div
          key={scene}
          className="absolute inset-0 z-10"
          initial={{ opacity: 0, scale: reduce ? 1 : 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: reduce ? 1 : 0.97 }}
          transition={{ duration: reduce ? 0.15 : 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <Active reduceMotion={!!reduce} />
        </motion.div>
      </AnimatePresence>

      {/* Play / pause control — required for autoplaying motion (WCAG 2.2.2). */}
      <button
        type="button"
        onClick={() => setPaused((p) => !p)}
        className="absolute bottom-3 right-3 z-30 inline-flex items-center gap-1.5 rounded-full border border-background/20 bg-background/10 hover:bg-background/20 transition-colors px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-background/80 backdrop-blur-sm"
        aria-label={paused ? "Play explainer" : "Pause explainer"}
        aria-pressed={paused}
      >
        {paused ? <Play className="w-3 h-3" aria-hidden="true" /> : <Pause className="w-3 h-3" aria-hidden="true" />}
        <span>{paused ? "play" : "pause"}</span>
      </button>

      {/* Progress strip */}
      <div className="absolute left-0 right-0 bottom-0 h-1 bg-background/10 z-30" aria-hidden="true">
        <div className="flex h-full gap-1 px-4 pb-1 pt-0.5">
          {script.scenes.map((s, i) => (
            <div key={i} className="flex-1 h-full rounded-full bg-background/20 overflow-hidden">
              <motion.div
                className="h-full bg-secondary origin-left"
                initial={false}
                animate={{
                  scaleX: i < scene ? 1 : i === scene ? (reduce ? 1 : 1) : 0,
                }}
                transition={{
                  duration:
                    i === scene && !reduce ? (s.durationMs ?? 3800) / 1000 : 0.3,
                  ease: "linear",
                }}
                style={{ scaleX: 0, transformOrigin: "left" }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Shared building-block components used by individual scripts ----------

export function SceneShell({
  kicker,
  title,
  body,
  children,
  reduceMotion,
}: {
  kicker?: string;
  title: React.ReactNode;
  body?: React.ReactNode;
  children?: React.ReactNode;
  reduceMotion: boolean;
}) {
  return (
    <div className="absolute inset-0 grid grid-cols-12 gap-6 p-10 md:p-14">
      <div className="col-span-7 flex flex-col justify-center">
        {kicker && (
          <motion.div
            className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-secondary mb-4"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: reduceMotion ? 0 : 0.1 }}
          >
            {kicker}
          </motion.div>
        )}
        <motion.h3
          className="font-display text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-balance"
          initial={{ opacity: 0, y: reduceMotion ? 0 : 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: reduceMotion ? 0 : 0.2, ease: [0.16, 1, 0.3, 1] }}
        >
          {title}
        </motion.h3>
        {body && (
          <motion.p
            className="mt-4 md:mt-5 max-w-md text-sm md:text-base text-background/70 leading-relaxed"
            initial={{ opacity: 0, y: reduceMotion ? 0 : 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: reduceMotion ? 0 : 0.45 }}
          >
            {body}
          </motion.p>
        )}
      </div>
      <div className="col-span-5 relative">{children}</div>
    </div>
  );
}

// Pulse-cursor terminal line, for code-y scenes
export function TerminalLine({
  prefix = "$",
  text,
  delay = 0,
  reduceMotion,
}: {
  prefix?: string;
  text: string;
  delay?: number;
  reduceMotion: boolean;
}) {
  const chars = useMemo(() => text.split(""), [text]);
  return (
    <div className="font-mono text-sm md:text-base text-background/85">
      <span className="text-secondary mr-2">{prefix}</span>
      {chars.map((c, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            duration: 0.01,
            delay: reduceMotion ? 0 : delay + i * 0.035,
          }}
        >
          {c}
        </motion.span>
      ))}
      <motion.span
        className="inline-block w-2 h-4 ml-0.5 align-middle bg-secondary"
        animate={{ opacity: [1, 0, 1] }}
        transition={{ duration: 0.9, repeat: Infinity }}
        aria-hidden="true"
      />
    </div>
  );
}

// Animated dot grid (used for "data" scenes)
export function DotGrid({
  rows = 6,
  cols = 9,
  reduceMotion,
}: {
  rows?: number;
  cols?: number;
  reduceMotion: boolean;
}) {
  const cells = Array.from({ length: rows * cols });
  return (
    <div
      className="absolute inset-0 grid"
      style={{
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gap: "8px",
        padding: "8px",
      }}
      aria-hidden="true"
    >
      {cells.map((_, i) => {
        const r = Math.floor(i / cols);
        const c = i % cols;
        const isFeature = (r + c) % 3 === 0;
        return (
          <motion.div
            key={i}
            className="rounded-full"
            style={{
              background: isFeature
                ? "var(--color-secondary)"
                : "rgba(255,255,255,0.25)",
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: 1,
              opacity: 1,
            }}
            transition={{
              duration: 0.45,
              delay: reduceMotion ? 0 : (r + c) * 0.04,
              ease: [0.16, 1, 0.3, 1],
            }}
          />
        );
      })}
    </div>
  );
}
